import { Button, ButtonGroup, Card, IconButton, LuIcon, Text, TextArea } from "@overmap-ai/blocks"
import { type FormIdentifier, type FormSubmission, OrganizationAccessLevel, type Stored } from "@overmap-ai/common"
import { useScanner } from "@overmap-ai/forms"
import { Pencil } from "lucide-react"
import { type FocusEventHandler, memo, useCallback, useEffect, useState } from "react"

import { useUserOrganizationAccess } from "../../hooks/users"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { selectLatestFormIdentifierValueOfFormSubmission } from "../../store/selectors/forms"

interface FormIdentifiersListItemProps {
	formSubmission: Stored<FormSubmission>
	formIdentifier: Stored<FormIdentifier>
}

export const FormIdentifiersListItem = memo<FormIdentifiersListItemProps>((props) => {
	const { formSubmission, formIdentifier } = props

	const openScanner = useScanner()
	const [isEditing, setIsEditing] = useState(false)
	const isBasic = useUserOrganizationAccess(OrganizationAccessLevel.BASIC)
	const [value, setValue] = useState("")
	const formIdentifierValue = useAppSelector(
		selectLatestFormIdentifierValueOfFormSubmission(formSubmission.uuid, formIdentifier.uuid),
	)

	useEffect(() => {
		setValue(formIdentifierValue?.value ?? "")
	}, [formIdentifierValue?.value])

	const handleCreate = useCallback(() => {
		void sdk.formIdentifierValues.add({
			value: value,
			form_submission: formSubmission.uuid,
			form_identifier: formIdentifier.uuid,
		})

		setIsEditing(false)
	}, [value, formIdentifier.uuid, formSubmission.uuid])

	const handleEdit = useCallback(() => {
		setIsEditing(true)
	}, [])

	const handleCancel = useCallback(() => {
		setIsEditing(false)
	}, [])

	const handleTextAreaChange = useCallback<FocusEventHandler<HTMLTextAreaElement>>((e) => {
		const value = e.target.value
		setValue(value)
	}, [])

	const handleScan = useCallback(() => {
		openScanner((close) => ({
			onDecodeResult: (result) => {
				const value = result.rawValue.trim()
				setValue(value)
				close()
			},
			onClose: close,
		}))
	}, [openScanner])

	return (
		<Card className="flex flex-col gap-2" variant="soft" size="sm">
			<div className="flex justify-between gap-2">
				<div className="flex items-center gap-2">
					<LuIcon icon={modelIconMapping.identifier} color="var(--base-a11)" />
					<Text accentColor="base" size="sm">
						{formIdentifier.name}
					</Text>
				</div>

				{!isEditing ? (
					<IconButton size="xs" accentColor="base" variant="ghost" onClick={handleEdit}>
						<LuIcon icon={Pencil} />
					</IconButton>
				) : (
					<ButtonGroup className="flex justify-end gap-2" size="xs" accentColor="base" variant="soft">
						<Button onClick={handleCancel}>Cancel</Button>
						<Button onClick={handleCreate}>Save</Button>
					</ButtonGroup>
				)}
			</div>

			{isEditing ? (
				<div className="flex w-full flex-col gap-4">
					<div className="flex w-full items-center gap-2">
						<TextArea
							className="field-sizing-content w-full"
							size="xs"
							readOnly={!isBasic}
							value={value}
							onChange={handleTextAreaChange}
						/>

						<IconButton
							onClick={handleScan}
							size="sm"
							variant="soft"
							accentColor="base"
							disabled={!isBasic}
						>
							<LuIcon icon={modelIconMapping.scan} />
						</IconButton>
					</div>
				</div>
			) : formIdentifierValue ? (
				<Text size="sm">{formIdentifierValue?.value}</Text>
			) : null}
		</Card>
	)
})
FormIdentifiersListItem.displayName = "FormIdentifiersListItem"
